/**
 * Admin-Reiter „News" (PV-037, anforderung.md „Produkt-News"): Liste (Entwürfe oben), Formular im
 * Modal (zwei Spalten DE | EN), KI-Übersetzung als Vorschlag, Veröffentlichen nur vollständig,
 * Zurückziehen, Löschen nach Rückfrage. Nur Voll-Admin (lib/admin-gating.ts).
 */
import {
  NEWS_TITEL_MAX,
  type NewsEntwurf,
  NewsEntwurfSchema,
  type NewsSprache,
  NewsVeroeffentlichbarSchema,
  type ProduktNews,
} from '@projektverwaltung/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ChangeEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/toast.js';
import { Badge } from '../components/v2/badge.js';
import { useConfirm } from '../components/v2/confirm-dialog.js';
import { Modal } from '../components/v2/modal.js';
import { ApiError, api } from '../lib/api.js';
import { formatZeitpunkt } from '../lib/format-date.js';
import { NEWS_QUERY_KEY, newsInSprache } from '../lib/news.js';

const ADMIN_NEWS_KEY = ['admin-news'] as const;

/** Welche Aktion einen Fehler ausgelöst hat — bestimmt, welche Meldung zu einem Status passt. */
type FehlerAktion = 'uebersetzen' | 'veroeffentlichen' | 'sonstige';

/**
 * Verständliche Meldung je HTTP-Status und Aktion; sonst nur der Titel der Aktion. Die
 * Übersetzungstexte (502/503/422 Schutzfilter) gelten NUR für „Übersetzen“ — bei allen anderen
 * Aktionen bedeutet 422 „unvollständig“ und 502/503 ein allgemeiner Serverfehler.
 */
function useFehlerText(): (err: unknown, aktion: FehlerAktion) => string | undefined {
  const { t } = useTranslation();
  return (err, aktion) => {
    if (!(err instanceof ApiError)) return undefined;
    if (aktion === 'uebersetzen') {
      switch (err.status) {
        case 503:
          return t('admin.news.fehler_ki_nicht_eingerichtet');
        case 502:
          return t('admin.news.fehler_uebersetzung');
        case 422:
          return t('admin.news.fehler_text_abgewiesen');
        default:
          return undefined;
      }
    }
    switch (err.status) {
      case 502:
      case 503:
        return t('admin.news.fehler_server');
      case 409:
        return aktion === 'veroeffentlichen'
          ? t('admin.news.fehler_bereits_veroeffentlicht')
          : undefined;
      case 404:
        return t('admin.news.fehler_nicht_gefunden');
      case 422:
        return t('admin.news.fehler_unvollstaendig');
      default:
        return undefined;
    }
  };
}
const LEER: NewsEntwurf = { titel_de: '', titel_en: '', text_de: '', text_en: '' };

const KNOPF =
  'rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';
const KNOPF_STIL = {
  background: 'var(--surface)',
  color: 'var(--ink-2)',
  border: '1px solid var(--line-soft)',
};
const KNOPF_PRIMAER = {
  background: 'var(--primary)',
  color: 'var(--surface)',
  border: '1px solid var(--primary)',
};
const FELD = 'w-full rounded-md border px-3 py-2 text-sm';
const FELD_STIL = {
  borderColor: 'var(--line-soft)',
  background: 'var(--surface)',
  color: 'var(--ink)',
};

export function NewsAdmin() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirmAction = useConfirm();
  const fehlerText = useFehlerText();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ADMIN_NEWS_KEY, queryFn: () => api.listAdminNews() });
  const [formular, setFormular] = useState<{ news: ProduktNews | null } | null>(null);
  const sprache: NewsSprache = i18n.language.startsWith('en') ? 'en' : 'de';

  const neuLaden = () => {
    void queryClient.invalidateQueries({ queryKey: ADMIN_NEWS_KEY });
    // Nutzersicht (Zählmarke, Hilfeseite) zieht nach.
    void queryClient.invalidateQueries({ queryKey: NEWS_QUERY_KEY });
  };
  // 404/409 = die Zeile ist veraltet (inzwischen gelöscht, veröffentlicht, zurückgezogen) → Liste neu laden.
  const fehler = (aktion: FehlerAktion) => (err: unknown) => {
    toast.error(t('admin.news.fehler'), fehlerText(err, aktion));
    if (err instanceof ApiError && (err.status === 404 || err.status === 409)) neuLaden();
  };
  const veroeffentlichen = useMutation({
    mutationFn: (id: string) => api.veroeffentlicheNews(id),
    onSuccess: () => {
      neuLaden();
      toast.success(t('admin.news.veroeffentlicht_toast'));
    },
    onError: fehler('veroeffentlichen'),
  });
  const zurueckziehen = useMutation({
    mutationFn: (id: string) => api.zieheNewsZurueck(id),
    onSuccess: () => {
      neuLaden();
      toast.success(t('admin.news.zurueckgezogen_toast'));
    },
    onError: fehler('sonstige'),
  });
  const loeschen = useMutation({
    mutationFn: (id: string) => api.deleteNews(id),
    onSuccess: () => {
      neuLaden();
      toast.success(t('admin.news.geloescht_toast'));
    },
    onError: fehler('sonstige'),
  });

  // Titel in der eingestellten Sprache, sonst der andere (Entwürfe haben oft nur eine Sprache).
  const anzeigeTitel = (n: ProduktNews): string => {
    const eigene = newsInSprache(n, sprache).titel;
    return eigene.trim() !== '' ? eigene : newsInSprache(n, sprache === 'de' ? 'en' : 'de').titel;
  };

  async function loescheNachRueckfrage(n: ProduktNews) {
    const ok = await confirmAction({
      title: t('admin.news.loeschen_titel'),
      body: t('admin.news.loeschen_text', { titel: anzeigeTitel(n) }),
      confirmLabel: t('admin.news.loeschen'),
      tone: 'danger',
    });
    if (ok) loeschen.mutate(n.news_id);
  }

  const items = query.data?.items ?? [];
  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold text-[color:var(--ink)]">
            {t('admin.news.titel')}
          </h2>
          <p className="mt-0.5 text-xs text-[color:var(--ink-3)]">{t('admin.news.untertitel')}</p>
        </div>
        <button
          type="button"
          className={KNOPF}
          style={KNOPF_PRIMAER}
          onClick={() => setFormular({ news: null })}
        >
          {t('admin.news.neu')}
        </button>
      </div>
      {query.isError ? (
        <p className="text-sm" style={{ color: 'var(--danger)' }}>
          {t('hilfe.seite.fehler')}
        </p>
      ) : query.isLoading ? (
        <p className="text-sm text-[color:var(--ink-3)]">{t('hilfe.seite.laedt')}</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-[color:var(--ink-3)]">{t('admin.news.leer')}</p>
      ) : (
        <ul className="divide-y rounded-lg border" style={{ borderColor: 'var(--line-soft)' }}>
          {items.map((n) => {
            const vollstaendig = NewsVeroeffentlichbarSchema.safeParse(n).success;
            return (
              <li
                key={n.news_id}
                className="flex items-center gap-3 px-4 py-3"
                style={{ borderColor: 'var(--line-soft)' }}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-[color:var(--ink)]">
                    {anzeigeTitel(n)}
                  </p>
                  <div className="mt-0.5 text-xs text-[color:var(--ink-3)]">
                    {n.veroeffentlicht_am ? (
                      t('admin.news.veroeffentlicht_am', {
                        datum: formatZeitpunkt(n.veroeffentlicht_am, i18n.language, 'datum'),
                      })
                    ) : (
                      <Badge tone="warn">{t('admin.news.entwurf')}</Badge>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className={KNOPF}
                  style={KNOPF_STIL}
                  onClick={() => setFormular({ news: n })}
                >
                  {t('admin.news.bearbeiten')}
                </button>
                {n.veroeffentlicht_am === null ? (
                  <button
                    type="button"
                    className={KNOPF}
                    style={KNOPF_PRIMAER}
                    disabled={!vollstaendig || veroeffentlichen.isPending}
                    title={vollstaendig ? undefined : t('admin.news.veroeffentlichen_hinweis')}
                    onClick={() => veroeffentlichen.mutate(n.news_id)}
                  >
                    {t('admin.news.veroeffentlichen')}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={KNOPF}
                    style={KNOPF_STIL}
                    disabled={zurueckziehen.isPending}
                    onClick={() => zurueckziehen.mutate(n.news_id)}
                  >
                    {t('admin.news.zurueckziehen')}
                  </button>
                )}
                <button
                  type="button"
                  className={KNOPF}
                  style={{ ...KNOPF_STIL, color: 'var(--danger)' }}
                  disabled={loeschen.isPending && loeschen.variables === n.news_id}
                  onClick={() => void loescheNachRueckfrage(n)}
                >
                  {t('admin.news.loeschen')}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {formular && (
        <NewsFormular
          news={formular.news}
          onClose={() => setFormular(null)}
          onGespeichert={() => {
            setFormular(null);
            neuLaden();
          }}
        />
      )}
    </section>
  );
}

function NewsFormular({
  news,
  onClose,
  onGespeichert,
}: {
  news: ProduktNews | null;
  onClose: () => void;
  onGespeichert: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const confirmAction = useConfirm();
  const fehlerText = useFehlerText();
  const titelId = useId();
  const [werte, setWerte] = useState<NewsEntwurf>(() =>
    news
      ? {
          titel_de: news.titel_de,
          titel_en: news.titel_en,
          text_de: news.text_de,
          text_en: news.text_en,
        }
      : LEER,
  );
  const [uebersetztNach, setUebersetztNach] = useState<NewsSprache | null>(null);
  const veroeffentlicht = news !== null && news.veroeffentlicht_am !== null;
  // Veröffentlicht: beide Sprachen müssen gefüllt bleiben (Server: 422); sonst eine reicht.
  const speicherbar = veroeffentlicht
    ? NewsVeroeffentlichbarSchema.safeParse(werte).success
    : NewsEntwurfSchema.safeParse(werte).success;

  const speichern = useMutation({
    mutationFn: () => (news ? api.updateNews(news.news_id, werte) : api.createNews(werte)),
    onSuccess: () => {
      toast.success(t('admin.news.gespeichert'));
      onGespeichert();
    },
    onError: (err) => toast.error(t('admin.news.fehler'), fehlerText(err, 'sonstige')),
  });

  const setze =
    (feld: keyof NewsEntwurf) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const wert = e.target.value;
      setWerte((w) => ({ ...w, [feld]: wert }));
    };
  const texteVon = (s: NewsSprache) =>
    s === 'de'
      ? { titel: werte.titel_de, text: werte.text_de }
      : { titel: werte.titel_en, text: werte.text_en };
  const quelleVollstaendig = (s: NewsSprache) => {
    const x = texteVon(s);
    return x.titel.trim() !== '' && x.text.trim() !== '';
  };

  // A10: Vorschlag in die andere Sprache. Vorhandene Texte nur nach Rückfrage überschreiben; bei
  // Fehler Meldung, die Felder bleiben unverändert.
  async function uebersetze(von: NewsSprache) {
    const nach: NewsSprache = von === 'de' ? 'en' : 'de';
    const ziel = texteVon(nach);
    if (ziel.titel.trim() !== '' || ziel.text.trim() !== '') {
      const ok = await confirmAction({
        title: t('admin.news.ueberschreiben_titel'),
        body: t('admin.news.ueberschreiben_text'),
        confirmLabel: t('admin.news.ueberschreiben_ok'),
        tone: 'danger',
      });
      if (!ok) return;
    }
    const quelle = texteVon(von);
    setUebersetztNach(nach);
    try {
      const ergebnis = await api.uebersetzeNews({ von, titel: quelle.titel, text: quelle.text });
      setWerte((w) =>
        nach === 'de'
          ? { ...w, titel_de: ergebnis.titel, text_de: ergebnis.text }
          : { ...w, titel_en: ergebnis.titel, text_en: ergebnis.text },
      );
    } catch (err) {
      toast.error(t('admin.news.uebersetzen_fehler'), fehlerText(err, 'uebersetzen'));
    } finally {
      setUebersetztNach(null);
    }
  }

  // Während der Übersetzung sind alle Felder gesperrt: weder Quelle noch Ziel ändern sich mitten im Aufruf.
  const laeuft = uebersetztNach !== null;
  const spalte = (s: NewsSprache) => {
    const de = s === 'de';
    const nach: NewsSprache = de ? 'en' : 'de';
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-[color:var(--ink)]">
          {de ? t('admin.news.spalte_de') : t('admin.news.spalte_en')}
        </h3>
        <input
          type="text"
          aria-label={de ? t('admin.news.titel_de') : t('admin.news.titel_en')}
          className={FELD}
          style={FELD_STIL}
          maxLength={NEWS_TITEL_MAX}
          value={de ? werte.titel_de : werte.titel_en}
          readOnly={laeuft}
          onChange={setze(de ? 'titel_de' : 'titel_en')}
        />
        <textarea
          aria-label={de ? t('admin.news.text_de') : t('admin.news.text_en')}
          className={`${FELD} font-mono`}
          style={FELD_STIL}
          rows={10}
          value={de ? werte.text_de : werte.text_en}
          readOnly={laeuft}
          onChange={setze(de ? 'text_de' : 'text_en')}
        />
        <button
          type="button"
          className={KNOPF}
          style={KNOPF_STIL}
          disabled={!quelleVollstaendig(s) || laeuft}
          onClick={() => void uebersetze(s)}
        >
          {uebersetztNach === nach
            ? t('admin.news.uebersetzt_laeuft')
            : de
              ? t('admin.news.nach_en')
              : t('admin.news.nach_de')}
        </button>
      </div>
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      labelledBy={titelId}
      closeOnBackdrop={false}
      closeOnEscape={false}
      panelClassName="w-full max-w-4xl max-h-[90vh] overflow-y-auto"
    >
      <div className="space-y-4 p-5">
        <h2 id={titelId} className="font-display text-lg font-semibold text-[color:var(--ink)]">
          {news ? t('admin.news.formular_bearbeiten') : t('admin.news.formular_neu')}
        </h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {spalte('de')}
          {spalte('en')}
        </div>
        <p className="text-xs text-[color:var(--ink-3)]">
          {veroeffentlicht
            ? t('admin.news.veroeffentlicht_hinweis')
            : t('admin.news.speichern_hinweis')}
        </p>
        <div className="flex justify-end gap-2">
          <button type="button" className={KNOPF} style={KNOPF_STIL} onClick={onClose}>
            {t('admin.news.abbrechen')}
          </button>
          <button
            type="button"
            className={KNOPF}
            style={KNOPF_PRIMAER}
            disabled={!speicherbar || speichern.isPending}
            onClick={() => speichern.mutate()}
          >
            {t('admin.news.speichern')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
